import { useLinkTo, useNavigation } from "@react-navigation/native";
import { BlurView } from "expo-blur";
import React, { memo, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import {
	Image,
	ImageBackground,
	StatusBar,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Button, Icon } from "react-native-elements";
import { SafeAreaView } from "react-native-safe-area-context";
import Swiper from "react-native-swiper";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchGetCommunityDetails } from "../actions/community";
import {
	CLEAR_INITIAL_LINK,
	CLEAR_SCAN,
	HIDE_QR_CODE_SCANNER_MODAL,
	HIDE_SPLASH_SCREEN,
	SET_DROP_DOWN_ALERT_ERROR,
	SHOW_QR_CODE_SCANNER_MODAL
} from "../actions/types";
import { getImage, getImageUri } from "../assets/Images";
import {
	ALL_ROUTES,
	LOGIN_REQUIRED_ROUTES,
	APPLE_STORE_URL,
	GOOGLE_PLAY_URL,
	SLOGAN
} from "../config/constants";
import { getLandingSelector } from "../selectors";
import { t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	isElectron,
	isNative,
	isSafari,
	SCREEN_HEIGHT,
	WHITE_COLOR
} from "../styles/commonStyles";
import { getRedirectLink, openURL } from "../utils/UrlUtil";
import QrCodeScannerModal from "./QrCodeScannerModal";
import SelectionGuide from "../components/SelectionGuide";
import AsyncStorage from "@react-native-async-storage/async-storage";

// Set while the user is invited to select a community. Kept outside of the
// component: the landing screen can be mounted again when coming back from the
// map, the list or the qr-code scanner.
const awaitingCommunityRef = { current: false };

const LandingScreen = () => {
	const dispatch = useDispatch();
	const linkTo = useLinkTo();
	// const route = useRoute();
	const navigation = useNavigation();

	// Anchors of the community selection guide (step 1)
	const qrAnchorRef = useRef();
	const mapAnchorRef = useRef();
	const listAnchorRef = useRef();
	const joinAnchorRef = useRef();
	// Displayed guide step: "select" (community) or "join"
	const [guideStep, setGuideStep] = useState(null);

	// Step 1 of the guide: select a community from the qr-code (mobile only),
	// the map or the list, all the tooltips displayed together
	const showSelectCommunityTooltips = (delay = 500) => {
		awaitingCommunityRef.current = true;
		setTimeout(() => setGuideStep("select"), delay);
	};

	// Step 2 of the guide: join the selected community
	const showJoinTooltip = (delay = 500) =>
		setTimeout(() => setGuideStep("join"), delay);

	// const registerkey = route?.params?.register;
	let {
		initialLink,
		link,
		currentCommunity,
		deeplink,
		showLoginModal,
		isLandingScreen
	} = useSelector(getLandingSelector, shallowEqual);

	useEffect(() => {
		(async () => {
			if (isNative) {
				dispatch({
					type: HIDE_SPLASH_SCREEN
				});
			}
			if (!currentCommunity) {
				showSelectCommunityTooltips();
			} else if (!(await AsyncStorage.getItem("hideTooltips"))) {
				showJoinTooltip();
			}
		})();
	}, []);

	// Step 2 once the community has been selected from the qr-code, the map or
	// the list (back on the landing screen)
	useEffect(() => {
		if (isLandingScreen && currentCommunity && awaitingCommunityRef.current) {
			showJoinTooltip();
		}
	}, [isLandingScreen, currentCommunity]);

	// The wait ends once the step 2 is displayed (not when it is scheduled: the
	// screen may be mounted again in the meantime)
	useEffect(() => {
		if (guideStep === "join") {
			awaitingCommunityRef.current = false;
		}
	}, [guideStep]);

	useEffect(() => {
		if (showLoginModal && currentCommunity) {
			navigateLoginModal();
		}
	}, [showLoginModal, currentCommunity]);

	useEffect(() => {
		if (initialLink && !isElectron) {
			if (
				LOGIN_REQUIRED_ROUTES.has(initialLink.match(/^\/([0-9a-zA-Z-]+)/)?.[1])
			) {
				// Login required (e.g. good deal url): the link is kept to be opened
				// once logged in (see Live)
				navigateLoginModal();
				return;
			}
			if (initialLink && initialLink !== "/" && !initialLink.startsWith("/?")) {
				try {
					linkTo(initialLink);
				} catch (error) {
					dispatch({
						type: SET_DROP_DOWN_ALERT_ERROR,
						error: `${t("alert:urlerror")} ${initialLink}`
					});
				}
			}

			dispatch({
				type: CLEAR_INITIAL_LINK
			});
		}
	}, [initialLink]);

	useEffect(() => {
		if (deeplink) {
			const link = getRedirectLink(deeplink);
			const regex = /^\/([0-9a-zA-Z-]+)$/;
			if (link) {
				if (regex.test(link) && !ALL_ROUTES.has(link.match(regex)[1])) {
					dispatch(fetchGetCommunityDetails(link.match(regex)[1]));
				} else {
					linkTo(link);
				}
			}
		}
	}, [deeplink]);
	useEffect(() => {
		if (link) {
			const regex = /^\/([0-9a-zA-Z-]+)\/?(.*)/;
			if (regex.test(link) && !ALL_ROUTES.has(link.match(regex)[1])) {
				dispatch(fetchGetCommunityDetails(link.match(regex)[1]));
			}
			linkTo(link);

			dispatch({
				type: CLEAR_SCAN
			});
		}
	}, [link]);

	const navigateLoginModal = () => {
		if (currentCommunity) {
			navigation.navigate("LoginModal");
		} else {
			// Guide the user to select a community first
			showSelectCommunityTooltips(0);
		}
	};

	const {
		bgColor1,
		bgColor2,
		darkgrey,
		white,
		flex1,
		fs18,
		fs25,
		font,
		fontBold,
		underline,
		p20,
		justifyContentCenter,
		alignItemsCenter,
		positionAbsolute,
		w100p,
		cover,
		rounded22,
		textCenter,
		mb20,
		m10,
		overflowHidden,
		opacity08
	} = commonStyles;

	const qrCodeIcon = (
		<Icon
			name="qrcode-scan"
			color={WHITE_COLOR}
			reverseColor="white"
			type="material-community"
			size={40}
		/>
	);

	const mapIcon = (
		<Icon
			raised
			reverse
			name="map-search-outline"
			color={COLOR2}
			reverseColor="white"
			type="material-community"
			size={28}
		/>
	);

	const listIcon = (
		<Icon
			raised
			reverse
			name="text-box-search-outline"
			color={COLOR2}
			reverseColor="white"
			type="material-community"
			size={30}
		/>
	);

	const joinButton = (
		<Button
			buttonStyle={[bgColor2]}
			containerStyle={[w100p]}
			activeOpacity={0.8}
			title={t("login:join")}
			onPress={navigateLoginModal}
			titleStyle={[white, font]}
		/>
	);

	// Step 2 of the guide
	const joinGuideSteps = [
		{
			key: "join",
			anchorRef: joinAnchorRef,
			renderAnchor: () => joinButton,
			text: t("login:tooltipextra"),
			placement: "top",
			width: 260,
			onPress: navigateLoginModal
		}
	];

	const selectionGuideSteps = [
		...(isNative || isMobile
			? [
					{
						key: "qrcode",
						anchorRef: qrAnchorRef,
						renderAnchor: () => qrCodeIcon,
						text: t("login:tooltip0"),
						placement: "left",
						onPress: () => dispatch({ type: SHOW_QR_CODE_SCANNER_MODAL })
					}
			  ]
			: []),
		{
			key: "map",
			anchorRef: mapAnchorRef,
			renderAnchor: () => mapIcon,
			text: isNative || isMobile ? t("login:tooltip1") : t("login:tooltip1bis"),
			placement: "bottom",
			onPress: () => navigation.navigate("CommunitiesMapModal")
		},
		{
			key: "list",
			anchorRef: listAnchorRef,
			renderAnchor: () => listIcon,
			text: t("login:tooltip2"),
			placement: "bottom",
			onPress: () => navigation.navigate("CommunitiesModal")
		}
	];

	return (
		<SafeAreaView style={[flex1, bgColor1]}>
			<StatusBar barStyle="dark-content" />
			<QrCodeScannerModal
				onClosed={() =>
					dispatch({
						type: HIDE_QR_CODE_SCANNER_MODAL
					})
				}
			/>
			<View style={[isSafari && { minHeight: SCREEN_HEIGHT / 2 }, flex1]}>
				<ImageBackground
					source={getImageUri("building")}
					style={[flex1, cover]}
				>
					<View
						style={[
							flex1,
							isNative && alignItemsCenter,
							isNative && justifyContentCenter
						]}
					>
						<TouchableOpacity
							style={[flex1]}
							onPress={() => navigation.navigate("AppInfosScreen")}
						>
							<Image
								style={[flex1, m10]}
								source={getImage("logo")}
								resizeMode="contain"
							/>
						</TouchableOpacity>
						{(isNative || isMobile) && (
							<TouchableOpacity
								ref={qrAnchorRef}
								style={[{ position: "absolute", right: 40, top: "50%" }]}
								onPress={() =>
									dispatch({
										type: SHOW_QR_CODE_SCANNER_MODAL
									})
								}
							>
								{isLandingScreen && qrCodeIcon}
							</TouchableOpacity>
						)}
					</View>
					<BlurView intensity={25} tint="light" style={flex1}>
						<Text style={[fontBold, darkgrey, fs25, textCenter, m10]}>
							{currentCommunity?.name ?? t("login:nocommunity")}
						</Text>
					</BlurView>
				</ImageBackground>
				<TouchableOpacity
					ref={mapAnchorRef}
					style={[
						positionAbsolute,
						{
							left: 20,
							bottom: 20
						}
					]}
					onPress={() => navigation.navigate("CommunitiesMapModal")}
				>
					{isLandingScreen && mapIcon}
				</TouchableOpacity>
				<TouchableOpacity
					ref={listAnchorRef}
					style={[
						positionAbsolute,
						{
							right: 20,
							bottom: 20
						}
					]}
					onPress={() => navigation.navigate("CommunitiesModal")}
				>
					{isLandingScreen && listIcon}
				</TouchableOpacity>
			</View>
			<View style={[flex1, p20]}>
				<Swiper
					loop={true}
					activeDotColor={COLOR2}
					dotColor={"rgba(255, 255, 255, 0.4)"}
					containerStyle={{
						marginBottom: 20,
						flex: 1,
						borderRadius: 22,
						backgroundColor: "white"
					}}
					controlsProps={{
						dotActiveStyle: { backgroundColor: COLOR2 },
						prevTitle: t("button:previous"),
						nextTitle: t("button:next"),
						prevTitleStyle: { color: COLOR2, fontFamily: "fontBold" },
						nextTitleStyle: { color: COLOR2, fontFamily: "fontBold" }
					}}
				>
					<View style={[flex1, rounded22, overflowHidden]}>
						<ImageBackground
							source={getImageUri("sharelife")}
							style={[
								flex1,
								cover,
								alignItemsCenter,
								justifyContentCenter,
								p20
							]}
							imageStyle={{ opacity: 0.9 }}
						>
							<Text style={[fontBold, white, fs25, textCenter, mb20]}>
								{SLOGAN}
							</Text>
						</ImageBackground>
					</View>
					<View style={[flex1, rounded22, overflowHidden]}>
						<ImageBackground
							source={getImageUri("help")}
							style={[
								flex1,
								cover,
								alignItemsCenter,
								justifyContentCenter,
								p20
							]}
						>
							<Text
								style={[fontBold, white, fs25, textCenter, mb20, opacity08]}
							>
								{t("login:help")}
							</Text>
						</ImageBackground>
					</View>
					<View style={[flex1, rounded22, overflowHidden]}>
						<ImageBackground
							source={getImageUri("jogging")}
							style={[
								flex1,
								cover,
								alignItemsCenter,
								justifyContentCenter,
								p20,
								{
									backgroundColor: "rgba(0, 0, 0, 0.1)"
								}
							]}
						>
							<Text style={[fontBold, white, fs25, textCenter, mb20]}>
								{t("login:find")}
							</Text>
						</ImageBackground>
					</View>
					<View style={[flex1, rounded22, overflowHidden]}>
						<ImageBackground
							source={getImageUri("tools")}
							style={[
								flex1,
								cover,
								alignItemsCenter,
								justifyContentCenter,
								p20
							]}
						>
							<Text style={[fontBold, white, fs25, textCenter, mb20]}>
								{t("login:share")}
							</Text>
						</ImageBackground>
					</View>
					<View style={[flex1, rounded22, overflowHidden]}>
						<ImageBackground
							source={getImageUri("vegetables")}
							style={[
								flex1,
								cover,
								alignItemsCenter,
								justifyContentCenter,
								p20
							]}
						>
							<Text style={[fontBold, white, fs25, textCenter, mb20]}>
								{t("login:localeconomy")}
							</Text>
						</ImageBackground>
					</View>
					<View style={[flex1, rounded22, overflowHidden]}>
						<ImageBackground
							source={getImageUri("building")}
							style={[
								flex1,
								cover,
								alignItemsCenter,
								justifyContentCenter,
								p20
							]}
						>
							<TouchableOpacity
								style={alignItemsCenter}
								onPress={() =>
									Platform.OS === "ios"
										? openURL(dispatch, APPLE_STORE_URL)
										: openURL(dispatch, GOOGLE_PLAY_URL)
								}
							>
								<Text style={[fontBold, white, fs25, textCenter, mb20]}>
									{t("login:like")}
								</Text>
								<Text style={[fontBold, fs18, white, underline]}>
									{t("login:rate")}
								</Text>
							</TouchableOpacity>
						</ImageBackground>
					</View>
				</Swiper>
				<View ref={joinAnchorRef} style={w100p} collapsable={false}>
					{joinButton}
				</View>
			</View>
			<SelectionGuide
				visible={Boolean(guideStep) && isLandingScreen}
				steps={guideStep === "join" ? joinGuideSteps : selectionGuideSteps}
				onClose={() => setGuideStep(null)}
			/>
		</SafeAreaView>
	);
};

const MemoizedLandingScreen = memo(LandingScreen);
export default MemoizedLandingScreen;
